import { apiRequest } from "./axios"
import {
  getName,
  getEmail,
  getEmailVerification,
  getProfileImage,
  type UserAccountData,
  type UserAccountRequest,
  type PrivyUserData,
  type PrivyUserResponse,
} from "./helper"
import { type Address } from "viem"
import type { UserProfileData } from "@/helpers/user-profile"
import { DiscordType } from "@/types/discord"

const updateDiscordChannelApi = async (reqObj: {
  items: AzuroSDK.BetslipItem[] | any[]
  type?: DiscordType
  //   wallet_address: `0x${string}`
}) => {
  const url = "/discord"
  try {
    const res = await apiRequest(url, "POST", {
      items: reqObj.items,
      type: reqObj.type || DiscordType.Azuro,
    })
    return res.data
  } catch (error) {
    throw error
  }
}

interface GetUserProfileDataResponse {
  data: {
    user: UserProfileData | null
  }
}

const getUserProfileDataApi = async (reqObj: {
  wallet_address: string
}): Promise<GetUserProfileDataResponse> => {
  const { wallet_address } = reqObj
  const url = `/profile-data?wallet_address=${encodeURIComponent(wallet_address)}`
  try {
    const res = await apiRequest(url, "GET")
    return res.data as GetUserProfileDataResponse
  } catch (error) {
    throw error
  }
}

interface ReferralResponse {
  items: { wallet_address: Address }[]
}

const getReferListApi = async (reqObj: {
  invite: string
}): Promise<ReferralResponse> => {
  const url = `/refer-list?invite=${encodeURIComponent(reqObj.invite)}`
  try {
    const res = await apiRequest(url, "GET")
    return res.data as ReferralResponse
  } catch (error) {
    throw error
  }
}

const updateUserProfileDataApi = async (
  user: any,
  forceManualUpdate = false
) => {
  // Helper to remove all non-letter characters
  const sanitizeName = (name: string | null | undefined) =>
    typeof name === "string" ? name.replace(/[^-\uFFFF\p{L}]/gu, "") : name

  // Helper to decide value based on manual flag
  const manualField = <T extends UserProfileData[keyof UserProfileData]>(
    field: keyof UserProfileData,
    manualFlag: keyof UserProfileData,
    fallback: T
  ): T => {
    if (!forceManualUpdate && currentProfile && currentProfile[manualFlag]) {
      return currentProfile[field] as T
    }
    return fallback
  }

  // Build the payload from a user object (e.g., Privy)
  const [firstName, lastName] = getName(user)
  const verification = getEmailVerification(user)

  // Fetch current profile data
  let currentProfile: UserProfileData | null = null
  try {
    const res = await getUserProfileDataApi({
      wallet_address: user.wallet_address,
    })
    currentProfile = res?.data?.user ?? null
  } catch (e) {
    currentProfile = null
  }

  let reqObj: any

  interface ManualFieldsUser {
    first_name: string
    display_name: string
    wallet_address: string
    [key: string]: any
  }

  const hasManualFields = (obj: unknown): obj is ManualFieldsUser => {
    return (
      obj !== null &&
      typeof obj === "object" &&
      "first_name" in obj &&
      "display_name" in obj &&
      "wallet_address" in obj
    )
  }

  if (hasManualFields(user)) {
    reqObj = { ...user }
    // Overwrite fields with currentProfile values if manual flag is true and not forceManualUpdate
    if (currentProfile && !forceManualUpdate) {
      const manualFields: [keyof UserProfileData, keyof UserProfileData][] = [
        ["display_name", "is_display_name_manual"],
        ["first_name", "is_first_name_manual"],
        ["last_name", "is_last_name_manual"],
        ["dob", "is_dob_manual"],
        ["gender", "is_gender_manual"],
        ["country", "is_country_manual"],
        ["city", "is_city_manual"],
        ["zip_code", "is_zip_code_manual"],
        ["address", "is_address_manual"],
        ["favorite_sports", "is_favorite_sports_manual"],
        ["profile_image", "is_profile_image_manual"],
      ]
      for (const [field, flag] of manualFields) {
        if (currentProfile[flag]) {
          reqObj[field] = currentProfile[field]
        }
      }
    }
  } else {
    reqObj = {
      wallet_address: user.wallet_address,
      email: getEmail(user),
      first_name: sanitizeName(
        manualField(
          "first_name",
          "is_first_name_manual",
          user.is_first_name_manual ? user.first_name : firstName
        )
      ),
      last_name: sanitizeName(
        manualField(
          "last_name",
          "is_last_name_manual",
          user.is_last_name_manual ? user.last_name : lastName
        )
      ),
      phone: user.phone?.number, // Set only if it was not null at login
      profile_image: manualField(
        "profile_image",
        "is_profile_image_manual",
        user.is_profile_image_manual
          ? user.profile_image
          : getProfileImage(user)
      ),
      verify_email_triggered: verification.verify_email_triggered,
      is_verified: verification.is_verified,
      display_name: manualField(
        "display_name",
        "is_display_name_manual",
        user.display_name
      ),
      dob: manualField("dob", "is_dob_manual", user.dob),
      gender: manualField("gender", "is_gender_manual", user.gender),
      country: manualField("country", "is_country_manual", user.country),
      city: manualField("city", "is_city_manual", user.city),
      zip_code: manualField("zip_code", "is_zip_code_manual", user.zip_code),
      address: manualField("address", "is_address_manual", user.address),
      favorite_sports: manualField(
        "favorite_sports",
        "is_favorite_sports_manual",
        user.favorite_sports
      ),
      is_display_name_manual: user.is_display_name_manual || false,
      is_first_name_manual: user.is_first_name_manual || false,
      is_last_name_manual: user.is_last_name_manual || false,
      is_dob_manual: user.is_dob_manual || false,
      is_gender_manual: user.is_gender_manual || false,
      is_country_manual: user.is_country_manual || false,
      is_city_manual: user.is_city_manual || false,
      is_zip_code_manual: user.is_zip_code_manual || false,
      is_address_manual: user.is_address_manual || false,
      is_favorite_sports_manual: user.is_favorite_sports_manual || false,
      is_profile_image_manual: user.is_profile_image_manual || false,
    }
  }

  // Remove keys with null or undefined values
  const filteredReqObj = Object.fromEntries(
    Object.entries(reqObj).filter(([_, v]) => v !== null && v !== undefined)
  )

  const url = "/profile-data"
  try {
    const res = await apiRequest(url, "POST", filteredReqObj)
    return res.data
  } catch (error) {
    throw error
  }
}

const createOrUpdateUserAccountApi = async ({
  user,
  walletAddress,
}: {
  user: UserAccountRequest
  walletAddress: string
}): Promise<UserAccountData> => {
  const url = "/account"
  const reqObj: Partial<UserAccountData> = Object.fromEntries(
    Object.entries({
      wallet_address: user.wallet_address,
      login_toolkit: user.login_toolkit,
      last_login_time: user.last_login_time,
      last_signin_at: user.last_signin_at,
      joined_on: user.joined_on,
      signin_token: user.signin_token,
      refer_code: user.refer_code,
      invite_code: user.invite_code,
      utm_source: user.utm_source,
      utm_medium: user.utm_medium,
      utm_campaign: user.utm_campaign,
      utm_term: user.utm_term,
      utm_content: user.utm_content,
      odds_preference: user.odds_preference,
      signed_msg_id: user.signed_msg_id,
      login_method: user.login_method,
    }).filter(([_, value]) => value !== undefined && value !== null) // Remove empty values
  )

  try {
    const res: any = await apiRequest(url, "POST", { ...reqObj, walletAddress })
    return res.data.user as UserAccountData
  } catch (error) {
    throw error
  }
}

const getUserAccountApi = async (
  reqObj: UserAccountRequest
): Promise<UserAccountData> => {
  const url = "/account"
  try {
    const res: any = await apiRequest(url, "POST", reqObj)
    return res.data.user as UserAccountData
  } catch (error) {
    throw error
  }
}

const createOrUpdatePrivyUserApi = async (
  user: any
): Promise<PrivyUserResponse> => {
  const url = "/user-privy"
  try {
    // Transform the user object to match database schema
    const userData: PrivyUserData = {
      did: user.id,
      createdAt: new Date(user.createdAt),
      linkedAccounts: JSON.stringify(user.linkedAccounts || []),
      email: user.email ? JSON.stringify(user.email) : null,
      phone: user.phone ? JSON.stringify(user.phone) : null,
      wallet: JSON.stringify(user.wallet),
      google: user.google ? JSON.stringify(user.google) : null,
      apple: user.apple ? JSON.stringify(user.apple) : null,
      twitter: user.twitter ? JSON.stringify(user.twitter) : null,
      discord: user.discord ? JSON.stringify(user.discord) : null,
      telegram: user.telegram ? JSON.stringify(user.telegram) : null,
      github: user.github ? JSON.stringify(user.github) : null,
      linkedin: user.linkedin ? JSON.stringify(user.linkedin) : null,
      spotify: user.spotify ? JSON.stringify(user.spotify) : null,
      instagram: user.instagram ? JSON.stringify(user.instagram) : null,
      tiktok: user.tiktok ? JSON.stringify(user.tiktok) : null,
      farcaster: user.farcaster ? JSON.stringify(user.farcaster) : null,
      delegatedWallets: JSON.stringify(user.delegatedWallets || []),
      mfaMethods: JSON.stringify(user.mfaMethods || []),
      hasAcceptedTerms: user.hasAcceptedTerms || false,
      isGuest: user.isGuest || false,
      wallet_address: user.wallet.address,
    }

    const res = await apiRequest<PrivyUserResponse>(url, "POST", userData)
    return res.data
  } catch (error) {
    throw error
  }
}

const getPrivyUserApi = async (
  walletAddress: string
): Promise<PrivyUserResponse> => {
  const url = `/user-privy?wallet_address=${walletAddress}`
  try {
    const res = await apiRequest<PrivyUserResponse>(url, "GET")
    return res.data
  } catch (error) {
    throw error
  }
}

const verifySignInTokenApi = async (data: {
  wallet_address: string
  token: string
}) => {
  const url = "/account/signin"
  try {
    const res = await apiRequest(url, "POST", data)
    return res.data
  } catch (error) {
    throw error
  }
}

const getSignedRulesStatus = async (
  walletAddress: string
): Promise<{ signed: boolean }> => {
  const url = `/rules/${walletAddress}`
  try {
    const res = await apiRequest<{ signed: boolean }>(url, "GET")
    return res.data
  } catch (error) {
    throw error
  }
}

const setSignMessage = async ({
  sign_token,
  walletAddress,
}: {
  sign_token: string
  walletAddress?: string
}) => {
  // if (walletAddress) {
  //   const url = '/sign-message-web3'
  //   try {
  //     const res = await apiRequest(url, 'POST', { sign_token, walletAddress })
  //     return res.data
  //   } catch (error) {
  //     throw error
  //   }
  // } else {
  const url = "/sign-message"
  try {
    const res = await apiRequest(url, "POST", { sign_token })
    return res.data
  } catch (error) {
    throw error
  }
  // }
}

/**
 * Validates if a referral code exists (case-insensitive) by calling the backend API.
 * @param code The referral code to validate
 * @returns {Promise<{ valid: boolean, msg?: string }>}
 */
export const validateInviteCodeApi = async (
  code: string
): Promise<{ valid: boolean; msg?: string }> => {
  const res = await fetch(
    `/api/account/validate-invite?code=${encodeURIComponent(code)}`
  )
  return res.json()
}

export {
  createOrUpdateUserAccountApi,
  updateDiscordChannelApi,
  getUserProfileDataApi,
  updateUserProfileDataApi,
  createOrUpdatePrivyUserApi,
  getPrivyUserApi,
  verifySignInTokenApi,
  getReferListApi,
  setSignMessage,
  getUserAccountApi,
  getSignedRulesStatus,
}
